import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {Chart, ChartOptions} from 'highcharts';
import {nextFrame} from 'app/core/helpers/dom';
import {COLORS_EXTENDED} from 'app/shared/classes/chart-colors';

@Injectable()
export class SyncChartService implements OnDestroy {
    private _charts: BehaviorSubject<Chart[]> = new BehaviorSubject([]);
    disabledSeries$ = new BehaviorSubject(new Set());
    private sub = Subscription.EMPTY;
    private isTooltipSyncEnabled = false;

    private destroyed = false;
    renderQueue = <{next: CallableFunction; component: any}[]>[];

    msgLimitLegends = false;

    colorsVisibleSeries = [...COLORS_EXTENDED];

    constructor(private zone: NgZone) {
        this.zone.runOutsideAngular(() => this.initRenderQueue());
    }

    // TODO: we need to extract it to other service i guess
    async initRenderQueue() {
        while (!this.destroyed) {
            await nextFrame();
            await this.runNextRender();
            await nextFrame();
        }
    }

    async runNextRender() {
        if (this.renderQueue.length === 0) {
            return;
        }
        const [{next}] = this.renderQueue.splice(0, 1);
        try {
            await next();
        } catch (e) {
            console.warn('this.renderQueue Error', e);
        }
    }

    get charts(): Chart[] {
        return this._charts.value;
    }

    addChart(chart: Chart): void {
        this._charts.next([...this._charts.value, chart]);
    }

    deleteChart(chartToDelete: Chart | ChartOptions): void {
        this._charts.next(this.charts.filter((chart: any) => chart && chart.renderTo !== (chartToDelete as ChartOptions)?.renderTo));
    }

    hideSeries(seriesName: string): void {
        this.disabledSeries$.getValue().add(seriesName);
        this.disabledSeries$.next(this.disabledSeries$.getValue());
    }

    showSeries(seriesName: string): void {
        this.disabledSeries$.getValue().delete(seriesName);
        this.disabledSeries$.next(this.disabledSeries$.getValue());
    }

    syncTooltips(enable = true) {
        this.isTooltipSyncEnabled = enable;
    }

    syncTooltip(event: any) {
        const charts = this.charts;
        for (let i = 0; i < this.charts.length; i++) {
            if (!charts[i].series) {
                continue;
            }

            const pointsArr = charts[i].series
                .map(series => {
                    if (series.visible) {
                        return series.data[event.x];
                    }
                })
                .filter(el => el);

            if (!pointsArr?.length) {
                continue;
            }

            charts[i].tooltip.refresh(pointsArr);
            charts[i].xAxis[0].drawCrosshair(null, event);
        }
    }

    hideTooltip() {
        const charts = this.charts;
        for (let i = 0; i < this.charts.length; i++) {
            if (!charts[i].series) {
                continue;
            }

            charts[i].tooltip.hide();
            charts[i].xAxis[0].hideCrosshair();
        }
    }

    onMouseEnter(event: any) {
        if (!this.isTooltipSyncEnabled) {
            return;
        }
        this.syncTooltip(event);
    }

    onMouseLeave(event: any) {
        if (!this.isTooltipSyncEnabled) {
            return;
        }
        this.hideTooltip();
    }

    ngOnDestroy(): void {
        this.renderQueue = [];
        this.sub.unsubscribe();
        this.destroyed = true;
    }
}
